import { type DatiProcura } from '@framework/api-client/api/inquiries-compile/schemas/datiProcura.ts';
import { type DatiTitolare } from '@framework/api-client/api/inquiries-compile/schemas/datiTitolare.ts';
import { type EnumDatiTitolareTipologia } from '@framework/api-client/api/inquiries-compile/schemas/enumDatiTitolareTipologia.ts';
import { type PersonalizzazioniRiepilogoDTO } from '@framework/api-client/api/inquiries-compile/schemas/personalizzazioniRiepilogoDTO.ts';
import { joinName } from '../../summaryUtils.ts';

/** Ruolo della persona nel contesto della richiesta */
export enum PersonRole {
  Applicant = 'applicant',
  LegalRepresentative = 'legalRepresentative',
  Attorney = 'attorney',
  /** Altri soggetti di `datiTitolari` (gestori, delegati moduli, ...) */
  Other = 'other',
}

/** Tipo di persona: fisica o giuridica */
export enum PersonType {
  Natural = 'natural',
  Legal = 'legal',
}

/** Dati anagrafici di un soggetto coinvolto nella richiesta */
export interface IPersonalDataItem {
  name: string;
  role: PersonRole;
  type?: PersonType;
  code: string;
  email?: string;
  pec?: string;
  id: string;
  /** Tipologia del soggetto, per gli item con ruolo `Other` */
  subjectType?: EnumDatiTitolareTipologia;
}

interface IGetPersonalDataItemsOptions {
  applicant?: DatiTitolare;
  attorney?: DatiProcura | null;
  /** Elementi di `datiTitolari` diversi dal titolare principale */
  otherSubjects?: DatiTitolare[];
  customizations?: PersonalizzazioniRiepilogoDTO | null;
}

/** Costruisce l'item per il soggetto giuridico (persona giuridica, ruolo titolare) */
function buildLegalEntityItem(
  legalEntity: NonNullable<DatiTitolare['soggettoGiuridico']>,
): IPersonalDataItem {
  return {
    id: 'soggettoGiuridico',
    role: PersonRole.Applicant,
    type: PersonType.Legal,
    name: legalEntity.nome ?? '-',
    code: legalEntity.partitaIva ?? '-',
    email: legalEntity.email ?? undefined,
    pec: legalEntity.pec ?? undefined,
  };
}

/**
 * Costruisce l'item per il titolare della richiesta.
 * Se è presente un soggetto giuridico, il titolare diventa legale rappresentante.
 */
function buildApplicantItem(
  applicant: DatiTitolare,
  isLegalRepresentative: boolean,
): IPersonalDataItem {
  return {
    id: 'titolare',
    role: isLegalRepresentative
      ? PersonRole.LegalRepresentative
      : PersonRole.Applicant,
    type: isLegalRepresentative ? undefined : PersonType.Natural,
    name: joinName(applicant.nome, applicant.cognome) ?? '-',
    code: applicant.codiceFiscale ?? '-',
    email: applicant.email ?? undefined,
    pec: applicant.pec ?? undefined,
  };
}

/** Costruisce l'item per un altro soggetto coinvolto nella richiesta */
function buildOtherSubjectItem(
  subject: DatiTitolare,
  index: number,
): IPersonalDataItem {
  return {
    // L'indice garantisce l'univocità anche a parità di codice fiscale
    id: `altroSoggetto-${index}-${subject.codiceFiscale ?? ''}`,
    role: PersonRole.Other,
    name: joinName(subject.nome, subject.cognome) ?? '-',
    code: subject.codiceFiscale ?? '-',
    email: subject.email ?? undefined,
    pec: subject.pec ?? undefined,
    subjectType: subject.tipologia ?? undefined,
  };
}

/** Costruisce l'item per il procuratore */
function buildAttorneyItem(attorney: DatiProcura): IPersonalDataItem {
  return {
    id: 'procuratore',
    role: PersonRole.Attorney,
    name: joinName(attorney.nome, attorney.cognome) ?? '-',
    code: attorney.codiceFiscale ?? '-',
    email: attorney.email ?? undefined,
  };
}

/**
 * Restituisce l'elenco dei dati anagrafici da visualizzare nel riepilogo,
 * filtrando in base alle customizzazioni (nascondi).
 * L'ordine è: soggetto giuridico, titolare/legale rappresentante, procuratore,
 * altri soggetti.
 */
export function getPersonalDataItems(
  options: IGetPersonalDataItemsOptions,
): IPersonalDataItem[] {
  const { applicant, attorney, otherSubjects, customizations } = options;
  const result: IPersonalDataItem[] = [];

  const legalEntity = customizations?.soggettoGiuridico?.nascondi
    ? undefined
    : applicant?.soggettoGiuridico;

  const shownApplicant = customizations?.titolare?.nascondi
    ? undefined
    : applicant;

  const shownAttorney = customizations?.procuratore?.nascondi
    ? undefined
    : attorney;

  if (legalEntity) {
    result.push(buildLegalEntityItem(legalEntity));
  }

  if (shownApplicant) {
    result.push(buildApplicantItem(shownApplicant, !!legalEntity));
  }

  if (shownAttorney) {
    result.push(buildAttorneyItem(shownAttorney));
  }

  // Il procuratore arriva da un endpoint dedicato e il titolare è già in
  // lista: senza questo filtro lo stesso soggetto comparirebbe due volte.
  // Si guarda a chi è stato effettivamente inserito: se una personalizzazione
  // lo nasconde il duplicato non esiste e il soggetto va mostrato.
  const alreadyShown = new Set(
    [shownApplicant?.codiceFiscale, shownAttorney?.codiceFiscale].filter(
      Boolean,
    ),
  );

  for (const [index, subject] of (otherSubjects ?? []).entries()) {
    if (subject.codiceFiscale && alreadyShown.has(subject.codiceFiscale)) {
      continue;
    }
    result.push(buildOtherSubjectItem(subject, index));
  }

  return result;
}
